import { Helmet } from 'react-helmet-async';

interface SEOProps {
    title?: string;
    description?: string;
    canonical?: string;
    ogType?: 'website' | 'article';
    ogImage?: string;
    twitterHandle?: string;
    keywords?: string;
    publishedTime?: string;
    modifiedTime?: string;
    author?: string;
}

const SEO = ({
    title,
    description = "Arrotech - Transforming Business with AI Solutions. Specialized in AI Automation, Conversational AI, and Research Intelligence.",
    canonical,
    ogType = 'website',
    ogImage = '/logo.png?v=2', // Fallback to our root logo
    twitterHandle = '@ArrotechAI',
    keywords = 'AI, Artificial Intelligence, Business Automation, Kenya Tech, Arrotech, AI Solutions, Machine Learning',
    publishedTime,
    modifiedTime,
    author = 'Arrotech Solutions'
}: SEOProps) => {
    const siteTitle = title ? `${title} | Arrotech` : 'Arrotech - AI Solutions Company';
    const siteUrl = 'https://arrotechsolutions.com';
    const fullCanonical = canonical ? `${siteUrl}${canonical}` : siteUrl;

    // JSON-LD Structured Data
    const organizationSchema = {
        "@context": "https://schema.org",
        "@type": "Organization",
        "name": "Arrotech",
        "url": siteUrl,
        "logo": `${siteUrl}/logo.png?v=2`,
        "sameAs": [
            "https://twitter.com/ArrotechAI",
            "https://instagram.com/arrotech",
            "https://tiktok.com/@arrotech_ai",
            "https://www.facebook.com/gachanjaharun",
            "https://youtube.com/@arrotech_ai"
        ],
        "contactPoint": {
            "@type": "ContactPoint",
            "email": "info@arrotechsolutions.com",
            "telephone": "+254-797-568564",
            "contactType": "customer service",
            "areaServed": "KE"
        },
        "description": description
    };

    const websiteSchema = {
        "@context": "https://schema.org",
        "@type": "WebSite",
        "name": "Arrotech",
        "alternateName": ["Arrotech Solutions", "Arrotech AI"],
        "url": siteUrl
    };
    return (
        <Helmet>
            {/* Standard Meta Tags */}
            <title>{siteTitle}</title>
            <meta name="description" content={description} />
            <meta name="keywords" content={keywords} />
            <meta name="author" content={author} />
            <link rel="canonical" href={fullCanonical} />

            {/* Open Graph / Facebook */}
            <meta property="og:type" content={ogType} />
            <meta property="og:title" content={siteTitle} />
            <meta property="og:description" content={description} />
            <meta property="og:image" content={`${siteUrl}${ogImage}`} />
            <meta property="og:url" content={fullCanonical} />
            <meta property="og:site_name" content="Arrotech" />

            {publishedTime && <meta property="article:published_time" content={publishedTime} />}
            {modifiedTime && <meta property="article:modified_time" content={modifiedTime} />}

            {/* Twitter */}
            <meta name="twitter:card" content="summary_large_image" />
            <meta name="twitter:creator" content={twitterHandle} />
            <meta name="twitter:title" content={siteTitle} />
            <meta name="twitter:description" content={description} />
            <meta name="twitter:image" content={`${siteUrl}${ogImage}`} />

            {/* Structured Data */}
            <script type="application/ld+json">
                {JSON.stringify(organizationSchema)}
            </script>
            <script type="application/ld+json">
                {JSON.stringify(websiteSchema)}
            </script>
        </Helmet>
    );
};

export default SEO;
